import { useEffect, useState } from 'react';
import { getErrorMessage } from '../../lib/errors';
import type { SlideThemeCatalogRead } from '../../types/api';
import { listSlideThemes } from './generationApi';

/** The catalogue's own default: what a run renders in when nothing is chosen. */
export const DEFAULT_THEME_SLUG = 'classic';

export interface SlideThemesResult {
  themes: SlideThemeCatalogRead[];
  isLoading: boolean;
  error: string | null;
}

/** Result of the last completed load, tagged with the filter it was loaded for. */
interface LoadedThemes {
  key: string;
  themes: SlideThemeCatalogRead[];
  error: string | null;
}

/** Stable empty result, so consumers do not see a new array identity per render. */
const NO_THEMES: never[] = [];

/** '' is "the whole catalogue" — the same thing an omitted `vibe` param means. */
function filterKey(vibeId?: string | null): string {
  return vibeId ?? '';
}

/**
 * The slide-theme catalogue, optionally narrowed to one vibe server-side.
 *
 * Same shape as {@link useVibes}: one effect, no synchronous state write, and a
 * failure is not fatal — the look is optional on every surface that offers it,
 * so an empty catalogue degrades to "auto" rather than to an error screen.
 *
 * The one difference is that the last list is **kept on screen while the next
 * one loads**. Changing the vibe on the generation form changes this filter,
 * and a grid that blanked between two lists would flicker on every keystroke's
 * worth of interaction.
 */
export function useSlideThemes(vibeId?: string | null): SlideThemesResult {
  const [loaded, setLoaded] = useState<LoadedThemes | null>(null);
  const key = filterKey(vibeId);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const themes = await listSlideThemes(key || null, controller.signal);
        if (cancelled) return;
        setLoaded({ key, themes, error: null });
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        setLoaded({
          key,
          themes: [],
          error: getErrorMessage(caught, 'Could not load the visual styles.'),
        });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [key]);

  const isCurrent = loaded !== null && loaded.key === key;

  return {
    themes: loaded?.themes ?? NO_THEMES,
    isLoading: !isCurrent,
    // A stale filter's error belongs to a list nobody asked for any more.
    error: isCurrent ? loaded.error : null,
  };
}

/**
 * The looks tagged for one vibe, for the surfaces that filter in the browser.
 *
 * A theme with no vibes suits every vibe, so it always survives the filter.
 * When nothing matches at all the whole catalogue comes back: an empty gallery
 * would read as "this vibe has no looks", which is a claim about the seeding
 * rather than about the request, and it would leave the user with no way to
 * change the look at all.
 */
export function themesForVibe(
  themes: readonly SlideThemeCatalogRead[],
  vibeId: string | null,
): SlideThemeCatalogRead[] {
  if (!vibeId) return [...themes];
  const matched = themes.filter(
    (theme) => theme.vibe_ids.length === 0 || theme.vibe_ids.includes(vibeId),
  );
  return matched.length > 0 ? matched : [...themes];
}

/**
 * True when a look is offered for this vibe — an untagged look suits all of
 * them. Lets a form drop a chosen look that the current vibe no longer offers,
 * rather than posting an id whose card is no longer on screen.
 */
export function themeSuitsVibe(theme: SlideThemeCatalogRead, vibeId: string | null): boolean {
  return !vibeId || theme.vibe_ids.length === 0 || theme.vibe_ids.includes(vibeId);
}

/**
 * The catalogue entry a rendered deck is drawn in.
 *
 * Matched on slug, not id: `slides[0].theme.name` is the slug the renderer
 * froze into the deck, and it is the only honest answer when the run picked its
 * theme automatically and left `theme_id` null.
 */
export function themeBySlug(
  themes: readonly SlideThemeCatalogRead[],
  slug: string | null,
): SlideThemeCatalogRead | null {
  if (!slug) return null;
  return themes.find((theme) => theme.slug === slug) ?? null;
}
